import { motion } from 'framer-motion';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import confetti from 'canvas-confetti';

const COLORS = ['#F97316', '#FBBF24', '#2D4A22', '#FB923C', '#FCD34D', '#4D7C0F'];

export const SpinnerWheel = ({ items, onSpinComplete, size = 400 }) => {
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const canvasRef = useRef(null);

  // Calculate dynamic size based on number of items
  const wheelSize = useMemo(() => {
    if (items.length > 16) return Math.max(size, 460);
    if (items.length > 10) return Math.max(size, 420);
    return size;
  }, [items.length, size]);

  const drawWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !items || items.length === 0) return;

    const ctx = canvas.getContext('2d');
    const centerX = wheelSize / 2;
    const centerY = wheelSize / 2;
    const radius = wheelSize / 2 - 2; // Minimal edge padding

    ctx.clearRect(0, 0, wheelSize, wheelSize);

    const sliceAngle = (2 * Math.PI) / items.length;
    
    // Larger, more readable font sizes
    const baseFontSize = items.length > 20 ? 13 
                       : items.length > 15 ? 15 
                       : items.length > 10 ? 17 
                       : 20;
    
    // Much smaller center circle for maximum text space
    const centerRadius = items.length > 15 ? 22 : 25;
    
    // Max text width so long names auto-scale to fit the slice
    const maxTextWidth = radius - centerRadius - 10;

    items.forEach((item, index) => {
      if (!item || !item.name) return;
      
      const startAngle = index * sliceAngle - Math.PI / 2;
      const endAngle = startAngle + sliceAngle;
      const midAngle = startAngle + sliceAngle / 2;

      // Draw slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.fillStyle = COLORS[index % COLORS.length];
      ctx.fill();
      ctx.strokeStyle = '#FDFBF7';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw text - positioned very close to edge
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(midAngle);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${baseFontSize}px Arial, sans-serif`;
      
      // Text shadow for visibility
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      
      // Position text just 4px from edge, scale down long names to fit slice
      ctx.fillText(item.name, radius - 4, 0, maxTextWidth);
      ctx.shadowBlur = 0;
      
      ctx.restore();
    });

    // Draw smaller center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, 2 * Math.PI);
    ctx.fillStyle = '#1C1917';
    ctx.fill();
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fill();
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 4;
    ctx.stroke();
  }, [items, wheelSize]);

  useEffect(() => {
    drawWheel();
  }, [drawWheel]);

  const spinWheel = useCallback(() => {
    if (isSpinning || items.length === 0) return;

    setIsSpinning(true);
    const spins = 5 + Math.random() * 5;
    const degrees = spins * 360 + Math.random() * 360;
    const finalRotation = rotation + degrees;

    setRotation(finalRotation);

    setTimeout(() => {
      const sliceAngle = 360 / items.length;
      const normalizedRotation = finalRotation % 360;
      const selectedIndex = Math.floor((360 - normalizedRotation + sliceAngle / 2) / sliceAngle) % items.length;
      const selected = items[selectedIndex];

      setIsSpinning(false);

      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#F97316', '#FBBF24', '#2D4A22']
      });

      onSpinComplete(selected);
    }, 4000);
  }, [isSpinning, items, rotation, onSpinComplete]);

  // Memoize animation config to prevent re-renders
  const animateConfig = useMemo(() => ({ rotate: rotation }), [rotation]);
  const transitionConfig = useMemo(() => ({ duration: 4, ease: 'easeOut' }), []);

  return (
    <div className="relative inline-block" data-testid="spinner-wheel-container">
      {/* Pointer */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
        <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-[#F97316]" />
      </div>

      {/* Wheel */}
      <motion.div
        animate={animateConfig}
        transition={transitionConfig}
        className="relative"
      >
        <canvas
          ref={canvasRef}
          width={wheelSize}
          height={wheelSize}
          className="rounded-full shadow-[0_16px_40px_rgba(249,115,22,0.3)]"
        />
      </motion.div>

      {/* Spin Button */}
      <button
        onClick={spinWheel}
        disabled={isSpinning || items.length === 0}
        data-testid="spin-wheel-button"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-br from-[#F97316] to-[#FBBF24] text-white px-8 py-3 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 font-['Outfit']"
      >
        {isSpinning ? 'Spinning...' : 'SPIN'}
      </button>
    </div>
  );
};